import type {
  PdfWorkerJob,
  PdfWorkerRequest,
  PdfWorkerResponse,
  ReelAssignment,
  ReelResult,
} from './types';
import type { Template, PdfLayoutPlan, FontSource } from '@wysiwyg/pdf-export';
import type { SerializableResolverContext } from './types';
import { DEFAULT_WORKER_COUNT, MAX_WORKER_COUNT } from './types';

export interface SharedWorkerData {
  template: Template;
  layout: PdfLayoutPlan;
  qrValues: string[];
  commonData: Record<string, any>;
  themeData: Record<string, any>;
  resolverContext: SerializableResolverContext;
  fontSources: FontSource[];
  baseUrl: string;
  defaultFont: string;
  isPreview: boolean;
}

export interface PoolOptions {
  workerCount?: number;
  onReelStart?: (reelNum: number) => void;
  onReelComplete?: (reelNum: number, totalReels: number) => void;
}

export class PdfWorkerPool {
  private workers: Worker[] = [];
  private idleWorkers: Worker[] = [];
  private queue: ReelAssignment[] = [];
  private resultMap: Map<number, ReelResult> = new Map();
  private completedCount = 0;
  private totalReels: number;
  private sealed = false;
  private enqueuedCount = 0;
  private hasRejected = false;

  private shared: SharedWorkerData;
  private options: PoolOptions;

  private resolveResults!: (results: ReelResult[]) => void;
  private rejectResults!: (err: Error) => void;
  private resultPromise: Promise<ReelResult[]>;

  // Ordered list of reelNums for final result ordering
  private reelOrder: number[] = [];

  constructor(shared: SharedWorkerData, options: PoolOptions, totalReels: number) {
    this.shared = shared;
    this.options = options;
    this.totalReels = totalReels;

    const workerCount = Math.min(
      Math.max(1, options.workerCount ?? DEFAULT_WORKER_COUNT),
      MAX_WORKER_COUNT,
      totalReels,
    );

    // Create workers
    for (let i = 0; i < workerCount; i++) {
      const worker = new Worker(
        new URL('./pdfWorker.ts', import.meta.url),
        { type: 'module' },
      );
      this.setupWorker(worker);
      this.workers.push(worker);
      this.idleWorkers.push(worker);
    }

    this.resultPromise = new Promise<ReelResult[]>((resolve, reject) => {
      this.resolveResults = resolve;
      this.rejectResults = reject;
    });
  }

  enqueue(reel: ReelAssignment): void {
    if (this.hasRejected || this.sealed) return;

    this.enqueuedCount++;
    this.reelOrder.push(reel.reelNum);

    if (this.idleWorkers.length > 0) {
      const worker = this.idleWorkers.pop()!;
      this.dispatchJob(worker, reel);
    } else {
      this.queue.push(reel);
    }
  }

  seal(): void {
    this.sealed = true;
    this.checkDone();
  }

  results(): Promise<ReelResult[]> {
    return this.resultPromise;
  }

  terminate(): void {
    for (const w of this.workers) {
      w.terminate();
    }
    this.workers = [];
    this.idleWorkers = [];
  }

  private setupWorker(worker: Worker): void {
    worker.onmessage = (e: MessageEvent<PdfWorkerResponse>) => {
      if (this.hasRejected) return;

      const response = e.data;

      if (response.type === 'error') {
        this.hasRejected = true;
        this.terminate();
        this.rejectResults(new Error(`Worker failed on reel ${response.reelNum}: ${response.error}`));
        return;
      }

      this.resultMap.set(response.reelNum, {
        reelNum: response.reelNum,
        pdfBytes: response.pdfBytes,
      });

      this.completedCount++;
      this.options.onReelComplete?.(response.reelNum, this.totalReels);

      if (this.queue.length > 0) {
        const next = this.queue.shift()!;
        this.dispatchJob(worker, next);
      } else {
        this.idleWorkers.push(worker);
        this.checkDone();
      }
    };

    worker.onerror = (err) => {
      if (this.hasRejected) return;
      this.hasRejected = true;
      this.terminate();
      this.rejectResults(new Error(`PDF worker error: ${err.message}`));
    };
  }

  private dispatchJob(worker: Worker, reel: ReelAssignment): void {
    const job: PdfWorkerJob = {
      reelNum: reel.reelNum,
      template: this.shared.template,
      layout: this.shared.layout,
      labelCount: reel.labelCount,
      qrIndexOffset: reel.qrIndexOffset,
      qrValues: this.shared.qrValues,
      commonData: this.shared.commonData,
      themeData: this.shared.themeData,
      resolverContext: this.shared.resolverContext,
      fontSources: this.shared.fontSources,
      baseUrl: this.shared.baseUrl,
      defaultFont: this.shared.defaultFont,
      isPreview: this.shared.isPreview,
    };

    const request: PdfWorkerRequest = { type: 'generate', job };
    this.options.onReelStart?.(reel.reelNum);
    worker.postMessage(request);
  }

  private checkDone(): void {
    if (this.sealed && this.completedCount === this.enqueuedCount) {
      this.terminate();
      // Return results in enqueue order
      const ordered: ReelResult[] = this.reelOrder.map((reelNum) => this.resultMap.get(reelNum)!);
      this.resolveResults(ordered);
    }
  }
}

export function getEffectiveWorkerCount(requested: number): number {
  const hardwareCap = typeof navigator !== 'undefined' && navigator.hardwareConcurrency
    ? navigator.hardwareConcurrency
    : 4;
  return Math.min(Math.max(1, requested), MAX_WORKER_COUNT, hardwareCap);
}

export function isWorkerSupported(): boolean {
  try {
    return typeof Worker !== 'undefined';
  } catch {
    return false;
  }
}
